/**
 * North toggle: the first press turns the map to north and remembers the
 * view it left; a second press, while the camera is still where north left
 * it, turns back to that view.
 */

export interface NorthView {
  bearing: number;
  pitch: number;
}

/** What the last North press left behind. */
export interface NorthMemory {
  /** View to return to. */
  saved: NorthView;
  /** View North turned to. Any other camera means the user rotated since. */
  landed: NorthView;
}

export type NorthStep =
  | { kind: "north"; target: NorthView; memory: NorthMemory }
  | { kind: "restore"; target: NorthView; memory: null }
  | { kind: "none"; memory: null };

/** Bearing or pitch closer than this counts as unchanged. */
export const NORTH_EPSILON_DEG = 0.5;

/** Bearing folded into (-180, 180]. */
export function normalizeBearing(bearing: number): number {
  const b = ((bearing % 360) + 360) % 360;
  return b > 180 ? b - 360 : b;
}

export function isNorth(bearing: number): boolean {
  return Math.abs(normalizeBearing(bearing)) < NORTH_EPSILON_DEG;
}

function sameView(a: NorthView, b: NorthView): boolean {
  return (
    Math.abs(normalizeBearing(a.bearing - b.bearing)) < NORTH_EPSILON_DEG &&
    Math.abs(a.pitch - b.pitch) < NORTH_EPSILON_DEG
  );
}

/**
 * One press of North. Turning to north keeps the pitch, so `landed` is the
 * current pitch at bearing 0; the saved view carries the pitch too, so a
 * tilted view comes back tilted.
 */
export function northToggle(current: NorthView, memory: NorthMemory | null): NorthStep {
  if (memory && sameView(current, memory.landed)) {
    return { kind: "restore", target: { ...memory.saved }, memory: null };
  }
  if (isNorth(current.bearing)) return { kind: "none", memory: null };
  const landed = { bearing: 0, pitch: current.pitch };
  return {
    kind: "north",
    target: { ...landed },
    memory: { saved: { bearing: current.bearing, pitch: current.pitch }, landed },
  };
}
